import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, Shield, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

const TermsOfService = () => {
  const lastUpdated = "January 25, 2025";

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          <div className="flex items-center space-x-3 mb-4">
            <FileText className="w-8 h-8 text-primary" />
            <h1 className="text-4xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-muted-foreground">
            Last updated: {lastUpdated}
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          {/* Introduction */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="w-5 h-5" />
                <span>Agreement to Terms</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                Welcome to CareKonnect, a web-based platform operated by the State of Minnesota 
                to facilitate care placement coordination between assisted living providers, 
                long-term care facilities, and placement professionals including case managers, 
                discharge planners, and social workers.
              </p>
              <p>
                By accessing or using CareKonnect, you agree to be bound by these Terms of Service 
                and all applicable laws and regulations. If you do not agree with any of these terms, 
                you are prohibited from using this platform.
              </p>
            </CardContent>
          </Card>

          {/* Definitions */}
          <Card>
            <CardHeader>
              <CardTitle>Definitions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">"Platform" or "Service"</h4>
                <p className="text-sm text-muted-foreground">
                  Refers to the CareKonnect web application and all related services.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">"Provider"</h4>
                <p className="text-sm text-muted-foreground">
                  Licensed assisted living facilities, long-term care facilities, and other 
                  care providers in Minnesota who post bed availability listings.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">"Placement Professional"</h4>
                <p className="text-sm text-muted-foreground">
                  County case managers, hospital discharge planners, social workers, and 
                  housing stabilization service workers who search for care placements.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">"Referral"</h4>
                <p className="text-sm text-muted-foreground">
                  A placement request submitted by a Placement Professional to a Provider 
                  for a specific client's care needs.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* User Accounts and Responsibilities */}
          <Card>
            <CardHeader>
              <CardTitle>User Accounts and Responsibilities</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Account Registration</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Users must provide accurate and complete information during registration</li>
                  <li>Providers must provide valid National Provider Identifier (NPI) numbers</li>
                  <li>Users are responsible for maintaining the confidentiality of their account credentials</li>
                  <li>Users must notify us immediately of any unauthorized use of their account</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Professional Verification</h4>
                <p className="text-sm text-muted-foreground">
                  The State of Minnesota reserves the right to verify professional credentials 
                  and licensing status of all users. Misrepresentation of professional status 
                  may result in immediate account termination.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Platform Usage */}
          <Card>
            <CardHeader>
              <CardTitle>Platform Usage</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Permitted Uses</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Posting accurate bed availability information (Providers only)</li>
                  <li>Searching for appropriate care placements (Placement Professionals)</li>
                  <li>Submitting legitimate referrals for clients requiring care</li>
                  <li>Professional communication regarding care coordination</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Prohibited Activities</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Posting false, misleading, or inaccurate information</li>
                  <li>Using the platform for commercial solicitation unrelated to care placement</li>
                  <li>Attempting to circumvent security measures or access restrictions</li>
                  <li>Sharing login credentials with unauthorized individuals</li>
                  <li>Using automated tools to scrape or harvest data from the platform</li>
                </ul>
              </div>
            </CardContent>
          </Card>

           {/* PHI/PII Avoidance */}
           <Card>
             <CardHeader>
               <CardTitle className="flex items-center space-x-2">
                 <Shield className="w-5 h-5" />
                 <span>PHI/PII Avoidance and Email-Only Approach</span>
               </CardTitle>
             </CardHeader>
             <CardContent className="space-y-4">
               <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                 <h4 className="font-semibold mb-2 text-blue-900">Email-Only Referral System</h4>
                 <p className="text-sm text-blue-800">
                   CareKonnect operates as an email-only referral coordination platform. Users must 
                   not include Protected Health Information (PHI) or Personally Identifiable 
                   Information (PII) in referral-related emails sent through CareKonnect.
                 </p>
               </div>
               <div>
                 <h4 className="font-semibold mb-2">User Responsibilities</h4>
                 <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                   <li>Do not include client names, dates of birth, or other identifying information</li>
                   <li>Use only general descriptors for care needs and support areas</li>
                   <li>Ensure all referral communications comply with your organization's privacy policies</li>
                   <li>Confirm understanding of PHI/PII restrictions before initiating referrals</li>
                 </ul>
               </div>
             </CardContent>
           </Card>

          {/* Subscription and Billing */}
          <Card>
            <CardHeader>
              <CardTitle>Subscription and Billing (Providers)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Subscription Plans</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Tier 1: $100/month for 1 location listing</li>
                  <li>Tier 2: $150/month for 2 location listings</li>
                  <li>Tier 3: $225/month for 3 locations + $50/month per additional location</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Billing Terms</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Subscriptions are billed monthly in advance</li>
                  <li>Payment is due on the subscription start date each month</li>
                  <li>Failed payments may result in suspension of dashboard access</li>
                  <li>Subscriptions can be canceled at any time through account settings</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Limitation of Liability */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5" />
                <span>Limitation of Liability</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold mb-2 text-orange-900">Important Notice</h4>
                <p className="text-sm text-orange-800">
                  CareKonnect is a facilitating platform only. The State of Minnesota does not 
                  guarantee the accuracy of posted information, availability of beds, or the 
                  quality of care provided by listed facilities.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Disclaimers</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Users are responsible for verifying all facility information independently</li>
                  <li>The platform does not constitute medical advice or recommendations</li>
                  <li>Placement decisions remain the sole responsibility of placement professionals</li>
                  <li>The State of Minnesota is not liable for placement outcomes or care quality</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Termination */}
          <Card>
            <CardHeader>
              <CardTitle>Account Termination</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                The State of Minnesota reserves the right to suspend or terminate user accounts 
                for violations of these Terms of Service, applicable laws, or professional standards. 
                Users may voluntarily terminate their accounts at any time through account settings.
              </p>
              <p className="text-sm text-muted-foreground">
                Upon termination, users' access to the platform will be revoked, but certain 
                information may be retained as required by law or for legitimate business purposes.
              </p>
            </CardContent>
          </Card>

          {/* Governing Law */}
          <Card>
            <CardHeader>
              <CardTitle>Governing Law and Jurisdiction</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                These Terms of Service are governed by the laws of the State of Minnesota. 
                Any disputes arising from use of this platform shall be resolved in the 
                appropriate courts of Minnesota.
              </p>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent>
              <p>
                If you have questions about these Terms of Service, please contact us at:
              </p>
              <div className="mt-4 space-y-2">
                <p><strong>Email:</strong> hellocarekonnect@gmail.com</p>
                <p><strong>Hours:</strong> Monday - Friday, 8:00 AM - 6:00 PM CST</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;